// InstallableRail — generic catalog + instances rail/grid/list.
//
// Renders one of three layouts of installable items (integrations,
// libraries, MCP connectors, plugins, templates) for a given scopeType.
//
// Props (set on the RAIL element, at the mount site):
//   domain        – 'integration'|'library'|'mcp'|'plugin'|'template'
//   scopeType     – 'org'|'workspace'|'project'
//                   NOTE: `scope` is a DOMQL v3.14 reserved keyword (CSS-variable
//                   scope mixin). Always use `scopeType` for this prop.
//   scopeId       – active org / workspace / project id
//   heading       – rail header copy, a bundle KEY or prose (optional).
//                   NOT `title`: that is a global HTML attribute, so the
//                   framework writes it on the rail node and the browser
//                   shows it as a tooltip over the whole rail
//                   (docs/I18N-CONVENTION.md §1.2). `title` is still read as
//                   the back-compat fallback.
//   emptyHint     – empty-state CTA copy, a bundle KEY or prose (optional)
//   layout        – 'rail'|'grid'|'list'  (default: 'rail')
//   catalogFilter – { category?, applicable_to?, … } extra catalogue filter.
//                   NOT `filter`: that is a CSS property, so css-in-props
//                   takes it for a style. `filter` is still read as the
//                   back-compat fallback.
//   onAddClick    – optional hook before modal opens
//   onInstanceClick – optional manage-deeplink override
//
// HOW A DESCENDANT READS A MOUNT PROP — ONE mechanism: `scope.railProp`.
// A mount prop lives on the rail ELEMENT, never in state. A descendant finds
// its rail with the framework's own `el.lookup` (the rail marks itself with
// `isInstallableRail`) and reads the prop off it, at any depth. This file used
// to carry three mechanisms for the same four props — sixteen
// `el.parent?.parent?.domain` forwards, state reads that no host ever
// satisfied (so the header title NEVER rendered), and a fixed four-hop walk
// added on top of both. Pinned in
// __tests__/installableRailPropDelivery.test.js through the real renderer.
//
// Seed props (offline/demo hosts — see marketplace/pages/installable-rail-
// demo.js). When `seedKinds` is set the rail renders it and performs NO
// network read and NO poll: a demo surface must not depend on a live
// backend, and it must not depend on a FAILING one either.
//   seedKinds     – array of catalogue rows to display instead of fetching
//   seedInstances – array of instance rows (optional, defaults to [])
//
// STATE THIS COMPONENT OWNS (WS-RAIL-ROOT-STATE-HANG-1, 2026-09-20). The four
// load slots live in the rail's OWN element state, never at root:
//   railLoading    boolean
//   railError      string|null
//   railKinds      array of catalogue rows
//   railInstances  array of instance rows
// They used to be root keys prefixed `_installableRail_<domain>_<scopeType>_*`
// — the prefix existed ONLY so two rails on one page could not collide, which
// element state gives for free. Root state was the wrong home for three
// measured reasons:
//   1. RE-ENTRANCY. `onInit` -> `loadRail` -> root write is a write that
//      re-renders the WHOLE page, including the rail's own mount site. Any
//      mount site that rebuilds its subtree from root state therefore
//      re-creates the rail, whose fresh `onInit` loads again — and the
//      in-flight guard cannot stop it, because the terminal
//      `loading: false` write releases the guard in the same turn. An
//      element-state write re-renders the rail's OWN subtree only, so the
//      rail can never re-create itself. The cycle has no edge left to close.
//   2. UNDECLARED ROOT KEYS. Every key here is computed
//      (`_installableRail_${domain}_${scopeType}_kinds`), so no state-defaults
//      tier can declare it and module scope splits it into two signals — a
//      write nobody reads. Element state has no such tier.
//   3. Two rails with the SAME domain+scopeType shared one key set and one
//      guard, so the second rail silently rendered the first one's data.
// Children read them off their inherited state: `s.railKinds`, `s.railLoading`
// (the rail's state node is the state every descendant inherits — nested
// `childrenAs: 'state'` rows excepted, and those never read these slots).
//
// Data contract (Phase 1 — display.* columns assumed present):
//   kind row: { kind, domain, category, display: { icon, label, tagline, color? },
//               applicable_to_scopes, enabled, … }
//   instance:  { kind, scope_type, scope_id, slug, display_name, enabled, … }
//
// Per-domain backend status (tickets/workspace.md "InstallableRail: 4 of 5
// domains have no instances backend" — audited 2026-08-08):
//   integration – real backend both halves. Catalogue = IntegrationKind
//                 (`GET /org-integrations/kinds`); instances =
//                 `GET /org-integrations` via listScopedIntegrations. REQUIRES
//                 orgId (see orgId contract below).
//   mcp         – real backend for INSTANCES ONLY: `/core/mcp-connectors`
//                 (workspace-scoped server-side from the caller's claims — no
//                 orgId needed, unlike integration). There is NO kinds
//                 catalogue for mcp (McpConnector rows are free-form
//                 name+url, not chosen from an enum) — IntegrationKind does
//                 not carry a `domain` field, so blindly reusing it here
//                 would silently show the integration catalogue (GitHub/
//                 Slack/…) mislabeled as MCP servers. Instead each installed
//                 connector is rendered as its OWN self-describing tile (see
//                 `_deriveSelfKinds`) — accurate, not a lie, no invented
//                 catalogue.
//   library/template/plugin – no server collection for either half. Any
//                 "installed" signal for library/template today lives in the
//                 marketplace package's per-workspace localStorage list
//                 (`marketplace.installed`), a LAYER ABOVE this shared
//                 component (shared may not import from a feature package —
//                 wrong dependency direction) — so this component correctly
//                 renders an honest empty catalogue for these three rather
//                 than a wrong or borrowed one. Wire a real reader here if/
//                 when a server collection exists for them.

// ── State key helpers ─────────────────────────────────────────────────

// The root-state key helpers that used to live here (_railKey/_getKinds/
// _getInstances/_isLoading/_getError) went with the root keys themselves —
// see "STATE THIS COMPONENT OWNS" above. `__installableRailInternals` never
// exported them, so nothing outside this file could read them.

const _hasInstalled = (instances, kind) => instances.some((i) => i.kind === kind)

// ── Data loading helpers ───────────────────────────────────────────────
// Keeps network calls out of the component declarations so they're
// plain async functions (allowed — they don't use el.*).
// (The poll interval moved into InstallableRail.scope.POLL_INTERVAL below —
// it's read from inside onInit, so it needed the same frank-trap fix as
// everything else here; nothing outside this file ever imported it.)

// Catalogue (integration_kinds) is served by the main server from Mongo
// (GET /org-integrations/kinds → IntegrationKind). Reuse the shared,
// migration-aware loader rather than talking to any data store directly.
// Both reads go through the shared integrations facade → SDK. The raw
// `fetch` + hand-rolled API-base/token helpers that used to live here were
// the last raw-transport leaf in this file (integrations remediation #6);
// they also hard-coded the wrong query contract — see _fetchInstances.
import { listIntegrationKinds, listScopedIntegrations } from '../functions/integrations/index.js'
// mcp connectors — real backend (`/core/mcp-connectors`), workspace-scoped
// server-side from the caller's claims (see McpConnectorController). Reused
// via the shared facade so this component never talks SDK/transport
// directly (matches the integration read above).
import { listConnectors } from '../functions/mcp/index.js'

// Domains with a real kinds/catalogue backend today. ONLY `integration`
// (IntegrationKind has no `domain` column — it IS the integration catalogue,
// nothing else). Reusing it for other domains would silently mislabel its
// rows (e.g. rendering GitHub/Slack/Notion as "Featured templates" — this
// was live in canvas DashboardRows.js's TemplatesRail (domain:'template')
// until this fix, since `_fetchKinds` never filtered by domain at all).
const _KIND_CATALOG_DOMAINS = new Set(['integration'])

// Catalogue read. The integration-kinds catalogue now lives in Mongo,
// served by the main server (GET /org-integrations/kinds). Delegate to the
// shared, migration-aware loader (`listIntegrationKinds`) and filter the
// catalogue down to the kinds applicable to this scopeType client-side.
// Returns [] for any domain with no real catalogue backend (see
// _KIND_CATALOG_DOMAINS) — an honest empty catalogue, never a borrowed one.
const _fetchKinds = async (domain, scopeType) => {
  if (typeof window === 'undefined') return []
  if (!_KIND_CATALOG_DOMAINS.has(domain)) return []
  try {
    const kinds = await listIntegrationKinds()
    return kinds.filter((k) => {
      if (k.enabled === false) return false
      const scopes = k.applicable_to_scopes || k.applicableToScopes
      if (scopeType && Array.isArray(scopes)) return scopes.includes(scopeType)
      return true
    })
  } catch (err) {
    console.warn('[InstallableRail] listIntegrationKinds failed', err && err.message)
    return []
  }
}

// Domains without a kinds catalogue (see _KIND_CATALOG_DOMAINS) but WITH a
// real instances backend (today: mcp) render each installed instance as its
// own self-describing tile — always "installed" — instead of intersecting
// against a catalogue that doesn't exist. Without this, `RailStrip` et al
// (which map over `kinds`, not `instances`) would show real installed
// connectors as if the domain had nothing at all — the exact silent-empty
// failure this ticket exists to stop, just moved one layer down.
const _deriveSelfKinds = (domain, instances) =>
  (instances || []).map((i) => ({
    kind: i.kind,
    domain,
    display: {
      icon: domain === 'mcp' ? 'terminal' : 'grid',
      label: i.display_name || i.kind,
      tagline: i._sourceUrl || ''
    },
    enabled: i.enabled !== false
  }))

// Domains whose "installed instances" have a real backend. `integration`
// (`GET /org-integrations`) and `mcp` (`GET /core/mcp-connectors`, mapped
// below) do. `library` / `plugin` / `template` do not — a full-monorepo
// route sweep (2026-08-02) found no such route in any domain module, and
// re-audited 2026-08-08: still true. Their "installed" signal today, if any,
// lives in the marketplace package's per-workspace localStorage list — a
// feature-package concern this shared component must not reach into (wrong
// dependency direction: shared is a dependency OF marketplace, never the
// reverse). See the file-header comment for the full per-domain audit.
const _INSTANCE_BACKED_DOMAINS = new Set(['integration', 'mcp'])
// The warn-once flag lives on `globalThis.__installableRailWarnedNoBackend`,
// never a module-scope `let`: frank revives the functions with no module
// scope, so a `let` read here is an unresolvable identifier that refuses
// every publish bundling this component (FA201, pointerCapability.js template).

// Fetch installed instances for a given scopeType — SDK-only (integrations
// remediation #6: the last raw-fetch leaf in this file).
//
// The previous raw fetch was ALSO wrong for `integration`, silently: it sent
// `scope_type` / `scope_id` (snake_case) and NO `orgId`, while
// GET /core/org-integrations requires `orgId` and reads camelCase
// `scopeType` / `scopeId` — so the server answered 400 `invalid_input` on
// every call and the `!res.ok` guard turned it into `[]`. The rail's
// installed half has been dead since it shipped. Routing through the shared
// `listScopedIntegrations` facade fixes the contract AND normalizes rows
// the same way every other integrations consumer sees them.
//
// `orgId` is a PER-DOMAIN requirement, not a blanket one:
//   integration – REQUIRED by GET /core/org-integrations. For `scopeType:
//     'org'` the scopeId IS the org id (that's how the production mount in
//     canvas DashboardRows resolves it); workspace/project scopes must pass
//     it explicitly via the `orgId` prop, resolved from `root.org` (NOT the
//     SDK's activeOrgId — that's a Promise-gated init flag with nothing
//     resolved yet on a bare cold load; `root.org` is what auth/boot URL-pins
//     from `/w/<org>` | `/<owner>/<key>` | `/studio/<owner>` before the SDK
//     has caught up — see reconcileMwtOrgFromUrl / workspaceUrlSync.js). A
//     workspace/project mount that omits it now THROWS in dev instead of
//     silently rendering empty (below) — this is the failure this ticket was
//     opened to stop.
//   mcp – NOT required. `/core/mcp-connectors` derives the tenant from the
//     caller's workspace claim server-side (McpConnectorController), so no
//     orgId is threaded through at all.
const _fetchInstances = async (domain, scopeType, scopeId, orgId) => {
  if (typeof window === 'undefined') return []
  // Skip while the scope hasn't resolved yet (e.g. scopeId not loaded).
  if (!scopeId || typeof scopeId === 'function') return []
  if (!_INSTANCE_BACKED_DOMAINS.has(domain)) {
    if (!globalThis.__installableRailWarnedNoBackend) {
      globalThis.__installableRailWarnedNoBackend = true
      console.warn(
        `[InstallableRail] no instances backend for domain "${domain}" — ` +
          'rendering catalogue only (see tickets/workspace.md "InstallableRail")'
      )
    }
    return []
  }

  if (domain === 'mcp') {
    // Workspace-scoped, claims-derived — org scope carries no scopeId filter
    // (org-scoped connectors are stored with scopeId:null; omitting the
    // param matches any scopeId within scopeType, which IS "org-scoped").
    const { data, error } = await listConnectors({
      scopeType,
      scopeId: scopeType === 'org' ? undefined : scopeId
    })
    if (error) throw new Error(error)
    return (data || []).map((row) => ({
      kind: row.id,
      slug: row.id,
      scope_type: row.scope_type,
      scope_id: row.scope_id,
      display_name: row.name,
      enabled: row.status === 'enabled',
      _sourceUrl: row.url
    }))
  }

  // integration — the only remaining orgId-requiring domain.
  const resolvedOrgId = orgId || (scopeType === 'org' ? scopeId : null)
  if (!resolvedOrgId) {
    if (scopeType !== 'org' && process.env.NODE_ENV !== 'production') {
      throw new Error(
        `[InstallableRail] domain="integration" scopeType="${scopeType}" mount is ` +
          'missing an orgId prop — pass orgId explicitly (dev-only hard fail; ' +
          'silently rendering empty is the exact failure tickets/workspace.md ' +
          '"InstallableRail" was opened to stop). Resolve it from root.org, not ' +
          'sdk.activeOrgId — see the _fetchInstances doc comment.'
      )
    }
    return []
  }
  try {
    return await listScopedIntegrations({
      type: scopeType,
      orgId: resolvedOrgId,
      id: scopeType === 'org' ? null : scopeId
    })
  } catch (err) {
    // Surface the failure instead of masking it as "nothing installed" —
    // KEY-IN-STATE: this throws into `scope.loadRail`'s catch, which writes
    // the message to the rail's own `railError` slot. Neither has an element;
    // the render site resolves.
    throw new Error(err?.message || 'apps.rail.error.loadInstalled')
  }
}

// `_loadRail` lived here (a root-state writer, duplicated into `scope` for the
// frank module-scope trap). It is gone with the root keys — `scope.loadRail`
// below is the only loader, and it writes the RAIL's own element state.

// ── Mount-prop readers ────────────────────────────────────────────────
// ONE object, set as the `scope` of every part that reads a mount prop AND
// spread into the rail's own scope. Each part carries it itself because a part
// can be mounted with NO rail above it — the UI-kit catalog mounts every
// component bare — and an element with no scoped ancestor has no `el.scope`
// at all: every `el.scope.x?.()` read in a text effect threw there (measured
// through the real renderer, __tests__/installableRailPropDelivery.test.js).
// A `scope:` VALUE is copied into each component when the module loads, so
// this survives frank; a bare read of the const from inside a factory would
// not (FA204).
const _railReaders = {
  // Bug C fix: DOMQL v3.14 props may be function-typed when accessed via
  // `el.X` in onInit — the framework doesn't auto-evaluate factory props
  // on access. Resolve here before passing to async fetch helpers so no
  // function literal ever reaches a URLSearchParams value.
  resolveElProp(el, s, prop) {
    const v = el[prop]
    return typeof v === 'function' ? v(el, s) : v
  },

  // THE RAIL ELEMENT, from the rail itself or from any descendant. The rail
  // marks itself (`isInstallableRail`), so the walk is the framework's own
  // `lookup` and has no depth to get wrong.
  railOf(el) {
    if (!el) return null
    if (el.isInstallableRail === true) return el
    return typeof el.lookup === 'function'
      ? el.lookup((node) => node.isInstallableRail === true)
      : null
  },

  // A MOUNT PROP LIVES ON THE RAIL ELEMENT, NEVER IN STATE. Every reader
  // inside the rail used to read `s.domain` / `s.title`, resolved
  // `undefined` in every host, and rendered a header with no title and a
  // sentence with no noun in it — found live on
  // /marketplace/installable-rail-demo, 2026-09-20. Read it off the rail,
  // through `resolveElProp`, with the RAIL's own state: a mount-site factory
  // (`scopeId: (el, s) => … s.owner`) was written against that state.
  //
  // NOT for a prop whose VALUE is a function (`onAddClick`): this cannot
  // tell a prop factory from a callback, and would invoke the host's
  // callback during a render. Read those raw off `railOf(el)`.
  railProp(el, s, prop) {
    const rail = this.railOf(el)
    if (!rail) return undefined
    return this.resolveElProp(rail, rail.state || s, prop)
  },

  // `heading` first, `title` as the back-compat fallback (see the props
  // header for why `title` cannot be the name).
  railHeading(el, s) {
    return this.railProp(el, s, 'heading') || this.railProp(el, s, 'title') || ''
  },

  // The empty hint is a WHOLE sentence per scope. The old single sentence
  // took the raw scopeType (`… for this {scope} yet.`), so the Georgian one
  // carried the English word `workspace` — and no placeholder can inflect a
  // Georgian noun. The keys stay LITERAL (docs/I18N-CONVENTION.md §1.3).
  SCOPE_HINT_KEYS: {
    workspace: 'apps.rail.emptyHint.workspace',
    org: 'apps.rail.emptyHint.org',
    project: 'apps.rail.emptyHint.project'
  },
  scopeHintKey(el, s) {
    const key = this.SCOPE_HINT_KEYS[this.railProp(el, s, 'scopeType')]
    return typeof key === 'string' ? key : this.SCOPE_HINT_KEYS.workspace
  },

  // The domain WORD is user-facing copy inside a translated frame, so it is
  // translated too. English pluralises `library` as `libraries`, never
  // `librarys`, so the plural comes from the NOUN and never from a suffix
  // on the placeholder; ka carries its own word for each. These are the App
  // Store's own nouns, already shipped in both languages. The keys stay
  // LITERAL: a key built by concatenation is invisible to the i18n parity
  // and orphan gates (docs/I18N-CONVENTION.md).
  DOMAIN_NOUNS: {
    integration: {
      one: 'marketplace.kind.integration.noun',
      many: 'marketplace.kind.integration.nouns'
    },
    library: { one: 'marketplace.kind.library.noun', many: 'marketplace.kind.library.nouns' },
    mcp: { one: 'marketplace.kind.mcp.noun', many: 'marketplace.kind.mcp.nouns' },
    plugin: { one: 'marketplace.kind.plugin.noun', many: 'marketplace.kind.plugin.nouns' },
    template: { one: 'marketplace.kind.template.noun', many: 'marketplace.kind.template.nouns' }
  },

  // The noun for a host that supplies no domain at all — the UI-kit catalog
  // mounts every component bare. A missing noun is not acceptable output,
  // so the sentence still names something.
  GENERIC_NOUN: { one: 'marketplace.kind.app.noun', many: 'marketplace.kind.app.nouns' },

  domainNoun(el, s, form) {
    const domain = this.railProp(el, s, 'domain')
    // Read the form off the entry rather than test the domain: an unknown
    // word (`constructor`) reaches Object.prototype through the map, and a
    // prototype member carries no `one`/`many`, so it falls through here.
    const entry = this.DOMAIN_NOUNS[domain]
    const key = (entry && entry[form]) || this.GENERIC_NOUN[form]
    const word = el.call('polyglot', key)
    // `translate` answers the KEY ITSELF for a key no bundle carries. Never
    // show that to a user — the raw domain word is at least a real noun.
    return word && word !== key ? word : domain || ''
  }
}

// ── Add button ────────────────────────────────────────────────────────

// Reviewed (border-habit sweep): KEPT — the dashed state-ring is the one
// sanctioned stroke (design-direction.md §8 / DESIGN_LANGUAGE §2.5 exception
// #2), and this button IS the "add new" affordance it signals. One of the
// two dashed "add new" states in this file; InstallableRailEmpty is the
// other.
export const InstallableRailAddButton = {
  scope: _railReaders,
  tag: 'button',
  type: 'button',
  flow: 'x',
  gap: 'Y',
  align: 'center center',
  minHeight: 'controlH',
  padding: '0 A',
  round: 'radiusPill',
  borderWidth: 'hairline',
  borderStyle: 'dashed',
  borderColor: 'line',
  background: 'transparent',
  // Raw-hue sweep (LMC-4, measured 2026-08-11 aa-contrast walk): `blue` is
  // the raw brand hue, tuned for a near-black ground — on the demo rail's
  // wash it measured 2.77:1 (rail layout) / 3.21:1 (grid/list layouts) in
  // light mode, below the 4.5:1 floor. `accentInk` is the same hue re-toned
  // as an adaptive [light,dark] pair. The `:hover` wash/border below stay
  // the raw hue — decoration on a known transparent ground, not ink.
  color: 'accentInk',
  fontSize: 'Z2',
  fontWeight: '700',
  cursor: 'pointer',
  flexShrink: '0',
  transition: 'B defaultBezier',
  transitionProperty: 'background, border-color',
  ':hover': {
    background: 'blue.1',
    borderColor: 'blue.5'
  },
  '@media(prefers-reduced-motion:reduce)': {
    transition: 'none'
  },

  Icon: {
    name: 'plus',
    fontSize: 'Z2',
    color: 'accentInk'
  },

  Label: {
    tag: 'span',
    // `{domain}` is the rail's own domain word ('integration', 'library', …).
    // It is a NOUN inside a translated frame, so it is translated too — see
    // `scope.domainNoun`.
    text: (el, s) =>
      el
        .call('polyglot', 'apps.rail.add')
        .replace(
          '{domain}',
          el.scope.domainNoun(el, s, 'one') || el.call('polyglot', 'marketplace.kind.app.noun')
        )
  },

  onClick: (e, el, s) => {
    // Resolved NOW, off the rail: a `scopeId` factory reads root state that can
    // land after the rail mounted, so a value captured at mount would open the
    // modal on no scope.
    const prop = (name) => el.scope.railProp(el, s, name)
    const domain = prop('domain')
    // `onAddClick` is a CALLBACK, so it is read raw off the rail and never
    // through `railProp`, which would invoke it as a prop factory.
    const onAddClick = el.scope.railOf(el)?.onAddClick
    if (typeof onAddClick === 'function') onAddClick(domain)
    el.call('openInstallableModal', {
      domain,
      defaultScope: { type: prop('scopeType'), id: prop('scopeId') },
      catalogFilter: prop('catalogFilter') ?? prop('filter')
    })
  }
}

// ── Empty state ───────────────────────────────────────────────────────

// Reviewed (border-habit sweep): KEPT — the other of the two sanctioned
// dashed "add new" state-rings (see InstallableRailAddButton above).
export const InstallableRailEmpty = {
  scope: _railReaders,
  flow: 'y',
  gap: 'Z',
  align: 'center center',
  padding: 'B A',
  round: 'radiusCard',
  borderWidth: 'hairline',
  borderStyle: 'dashed',
  borderColor: 'line',
  background: 'overlayStrong',
  width: '100%',

  EmptyIcon: {
    extends: 'Icon',
    name: (el, s) => {
      const icons = {
        integration: 'grid',
        library: 'bookmark',
        mcp: 'terminal',
        plugin: 'puzzle',
        template: 'layers'
      }
      // Same map read as `domainNoun`: take the VALUE and check it, so an
      // unknown domain cannot reach a prototype member.
      const icon = icons[el.scope.railProp(el, s, 'domain')]
      return typeof icon === 'string' ? icon : 'grid'
    },
    fontSize: 'D',
    color: 'caption'
  },

  EmptyLabel: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'title',
    textAlign: 'center',
    text: (el, s) => {
      const emptyHint = el.scope.railProp(el, s, 'emptyHint')
      return emptyHint
        ? el.call('polyglot', emptyHint)
        : el.call('polyglot', 'apps.rail.empty.title').replace(
            '{domain}',
            // No scope means no rail above this mount (the UI-kit catalog
            // mounts the component bare), and the generic noun keeps the
            // sentence whole.
            el.scope.domainNoun(el, s, 'one') || el.call('polyglot', 'marketplace.kind.app.noun')
          )
    }
  },

  EmptyHint: {
    tag: 'p',
    margin: '0',
    fontSize: 'Y2',
    fontWeight: '400',
    color: 'caption',
    textAlign: 'center',
    text: (el, s) =>
      el
        .call(
          'polyglot',
          // No scope means no rail above this mount (the UI-kit catalog mounts
          // the component bare); `workspace` is the default it always had.
          el.scope.scopeHintKey(el, s)
        )
        .replace(
          '{domain}',
          el.scope.domainNoun(el, s, 'many') || el.call('polyglot', 'marketplace.kind.app.nouns')
        )
  },

  AddButton: {
    extends: 'InstallableRailAddButton'
  }
}

// ── Loading skeleton ──────────────────────────────────────────────────

export const InstallableRailSkeleton = {
  flow: 'x',
  gap: 'Z',
  align: 'center left',

  childExtends: {
    round: 'radiusControl',
    background: 'line',
    height: 'B',
    width: 'E',
    flexShrink: '0',
    // Pulse animation via opacity — respects prefers-reduced-motion at
    // the keyframe level (no JS toggle needed).
    animation: 'pulse 1.5s ease-in-out infinite',
    '@media(prefers-reduced-motion:reduce)': {
      animation: 'none',
      opacity: '0.5'
    }
  },

  Item_1: { width: 'E' },
  Item_2: { width: 'D2', opacity: '0.8' },
  Item_3: { width: 'D2', opacity: '0.6' }
}

// ── Rail header ───────────────────────────────────────────────────────

export const InstallableRailHeader = {
  scope: _railReaders,
  flow: 'x',
  gap: 'Z',
  align: 'center space-between',

  TitleGroup: {
    flow: 'x',
    gap: 'Y',
    align: 'center left',

    TitleIcon: {
      extends: 'Icon',
      name: (el, s) => {
        const icons = {
          integration: 'grid',
          library: 'bookmark',
          mcp: 'terminal',
          plugin: 'puzzle',
          template: 'layers'
        }
        const icon = icons[el.scope.railProp(el, s, 'domain')]
        return typeof icon === 'string' ? icon : 'grid'
      },
      fontSize: 'B',
      color: 'caption',
      hide: (el, s) => !el.scope.railProp(el, s, 'domain')
    },

    Title: {
      tag: 'h3',
      margin: '0',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'title',
      hide: (el, s) => !el.scope.railHeading(el, s),
      // A mount site passes a bundle KEY or prose as the rail `heading`
      // (canvas DashboardRows.js passes keys), so the render site resolves it —
      // the same shape `EmptyState`, `TutorialGifNotification` and
      // `InstallableRailItem`'s tagline already use. `translate()` returns its
      // input unchanged, so a caller that passes prose is untouched.
      text: (el, s) => el.call('polyglot', el.scope.railHeading(el, s) || '')
    }
  },

  AddButton: {
    extends: 'InstallableRailAddButton',
    // In list layout hide from header — the list footer carries it.
    hide: (el, s) => (el.scope.railProp(el, s, 'layout') || 'rail') === 'list'
  }
}

// ── Main export ───────────────────────────────────────────────────────

export const InstallableRail = {
  // FA204 / frank trap (workspace d6ea1142, project_frank_drops_module_local_
  // helpers) — every helper this component's factories/lifecycle hooks read
  // used to be a bare module-scope const/function above; it read as
  // undefined once frank.toJSON'd for platform delivery. Duplicated here
  // (not shared with the module-scope originals) because those originals
  // stay module-scope ONLY to back `__installableRailInternals` (below) —
  // the offline unit test's seam onto the per-domain backend audit — and a
  // shared `warnedMissingDomainBackend` "once" flag would otherwise let a
  // test call and a live mount silence each other's warning. Every OTHER
  // InstallableRail factory below is scope-only.
  scope: {
    ..._railReaders,
    POLL_INTERVAL: 30_000,
    KIND_CATALOG_DOMAINS: new Set(['integration']),
    INSTANCE_BACKED_DOMAINS: new Set(['integration', 'mcp']),
    warnedMissingDomainBackend: false,

    hasInstalled(instances, kind) {
      return instances.some((i) => i.kind === kind)
    },

    // The three layout strips differ by ONE word. `hide` and `children` were
    // written out three times each.
    stripHidden(el, s, layout) {
      if ((this.railProp(el, s, 'layout') || 'rail') !== layout) return true
      return s.railLoading || s.railKinds.length === 0
    },
    stripRows(el, s, layout) {
      const domain = this.railProp(el, s, 'domain')
      return s.railKinds.map((k) => ({
        domain,
        kind: k.kind,
        display: k.display || { icon: k.icon, label: k.label, tagline: k.description },
        installed: this.hasInstalled(s.railInstances, k.kind),
        _layout: layout
      }))
    },

    // Resolve the four load inputs FRESH off the rail, then load. `onInit`,
    // the poll tick and Retry all come through here, so none of them can pass
    // an unresolved factory to the loader.
    reload(rail) {
      const s = rail.state
      return this.loadRail(
        rail,
        this.resolveElProp(rail, s, 'domain'),
        this.resolveElProp(rail, s, 'scopeType'),
        this.resolveElProp(rail, s, 'scopeId'),
        this.resolveElProp(rail, s, 'orgId')
      )
    },

    // See the module-scope `_deriveSelfKinds` above for the full reasoning
    // (identical logic, duplicated per the file-header note).
    deriveSelfKinds(domain, instances) {
      return (instances || []).map((i) => ({
        kind: i.kind,
        domain,
        display: {
          icon: domain === 'mcp' ? 'terminal' : 'grid',
          label: i.display_name || i.kind,
          tagline: i._sourceUrl || ''
        },
        enabled: i.enabled !== false
      }))
    },

    // See the module-scope `_fetchKinds` above for the full reasoning
    // (identical logic, duplicated per the file-header note).
    async fetchKinds(domain, scopeType) {
      if (typeof window === 'undefined') return []
      if (!this.KIND_CATALOG_DOMAINS.has(domain)) return []
      try {
        const kinds = await listIntegrationKinds()
        return kinds.filter((k) => {
          if (k.enabled === false) return false
          const scopes = k.applicable_to_scopes || k.applicableToScopes
          if (scopeType && Array.isArray(scopes)) return scopes.includes(scopeType)
          return true
        })
      } catch (err) {
        console.warn('[InstallableRail] listIntegrationKinds failed', err && err.message)
        return []
      }
    },

    // See the module-scope `_fetchInstances` above for the full reasoning
    // (identical logic, duplicated per the file-header note).
    async fetchInstances(domain, scopeType, scopeId, orgId) {
      if (typeof window === 'undefined') return []
      if (!scopeId || typeof scopeId === 'function') return []
      if (!this.INSTANCE_BACKED_DOMAINS.has(domain)) {
        if (!this.warnedMissingDomainBackend) {
          this.warnedMissingDomainBackend = true
          console.warn(
            `[InstallableRail] no instances backend for domain "${domain}" — ` +
              'rendering catalogue only (see tickets/workspace.md "InstallableRail")'
          )
        }
        return []
      }

      if (domain === 'mcp') {
        const { data, error } = await listConnectors({
          scopeType,
          scopeId: scopeType === 'org' ? undefined : scopeId
        })
        if (error) throw new Error(error)
        return (data || []).map((row) => ({
          kind: row.id,
          slug: row.id,
          scope_type: row.scope_type,
          scope_id: row.scope_id,
          display_name: row.name,
          enabled: row.status === 'enabled',
          _sourceUrl: row.url
        }))
      }

      // integration — the only remaining orgId-requiring domain.
      const resolvedOrgId = orgId || (scopeType === 'org' ? scopeId : null)
      if (!resolvedOrgId) {
        if (scopeType !== 'org' && process.env.NODE_ENV !== 'production') {
          throw new Error(
            `[InstallableRail] domain="integration" scopeType="${scopeType}" mount is ` +
              'missing an orgId prop — pass orgId explicitly (dev-only hard fail; ' +
              'silently rendering empty is the exact failure tickets/workspace.md ' +
              '"InstallableRail" was opened to stop). Resolve it from root.org, not ' +
              'sdk.activeOrgId — see the fetchInstances doc comment.'
          )
        }
        return []
      }
      try {
        return await listScopedIntegrations({
          type: scopeType,
          orgId: resolvedOrgId,
          id: scopeType === 'org' ? null : scopeId
        })
      } catch (err) {
        // KEY-IN-STATE: this throws into `loadRail`'s catch, which writes the
        // message to the rail's own `railError` slot. Neither has an element;
        // the render site resolves.
        throw new Error(err?.message || 'apps.rail.error.loadInstalled')
      }
    },

    // THE LOADER. Takes the rail ELEMENT and writes the rail's OWN state —
    // never root state. A root write re-renders the whole page and can
    // re-create this very element, which re-runs `onInit` and loads again;
    // the terminal `railLoading: false` write releases the in-flight guard in
    // the same turn, so that cycle never settles (WS-RAIL-ROOT-STATE-HANG-1).
    // An element-state write re-renders this rail's subtree only.
    async loadRail(el, domain, scopeType, scopeId, orgId) {
      if (!el?.state?.update || !domain || !scopeType) return
      // A load that resolves after the rail was removed must not write —
      // `onDestroy` sets the flag, and the write would otherwise touch a
      // state node that is no longer mounted.
      if (el._railGone) return
      if (el.state.railLoading) return
      el.state.update({ railLoading: true, railError: null })

      try {
        const [kinds, instances] = await Promise.all([
          this.fetchKinds(domain, scopeType),
          this.fetchInstances(domain, scopeType, scopeId, orgId)
        ])
        const resolvedKinds =
          !this.KIND_CATALOG_DOMAINS.has(domain) && this.INSTANCE_BACKED_DOMAINS.has(domain)
            ? this.deriveSelfKinds(domain, instances)
            : kinds
        if (el._railGone || !el.state?.update) return
        el.state.update({
          railKinds: resolvedKinds,
          railInstances: instances,
          railLoading: false
        })
      } catch (err) {
        if (el._railGone || !el.state?.update) return
        el.state.update({
          railLoading: false,
          railError: err?.message || 'apps.rail.error.load'
        })
      }
    }
  },

  flow: 'y',
  gap: 'Z',

  // What `scope.railOf` looks for. A plain prop, so it survives frank and a
  // mount site that names the rail anything (`IntegrationsRail`, `Rail_2`).
  isInstallableRail: true,

  // The rail's OWN load slots. Declared, so every descendant inherits real
  // values on the first render instead of reading `undefined` off root.
  // A mount site's own state keys still reach the descendants (state
  // inheritance), so `scopeId: (el, s) => … s.owner` at a call site keeps
  // working — measured, not assumed.
  state: {
    railLoading: false,
    railError: null,
    railKinds: [],
    railInstances: []
  },

  // ── Initialise: load catalogue + instances, start poll ──────────────
  onInit: (el, s) => {
    // (`orgId` is REQUIRED by GET /core/org-integrations. Mounts that use a
    // workspace/project scope must pass it explicitly; an `org` scope's
    // scopeId already IS the org id — resolved inside scope.fetchInstances.)
    const domain = el.scope.resolveElProp(el, s, 'domain')
    const scopeType = el.scope.resolveElProp(el, s, 'scopeType')
    if (!domain || !scopeType) return

    // Offline/demo host: render the seed, read nothing, poll nothing.
    const seedKinds = el.scope.resolveElProp(el, s, 'seedKinds')
    if (seedKinds) {
      el.state.update({
        railKinds: seedKinds,
        railInstances: el.scope.resolveElProp(el, s, 'seedInstances') || [],
        railLoading: false,
        railError: null
      })
      return
    }

    // Kick off the first fetch.
    el.scope.reload(el)

    // Poll every 30 s as fallback realtime. `reload` resolves the inputs again
    // on each tick — an org id can land after the first render (lazy auth
    // hydration). The timer id lives on the element (transient; not state).
    el._railPollTimer = setInterval(() => el.scope.reload(el), el.scope.POLL_INTERVAL)
  },

  // Clean up poll timer on destroy, and close the write window for any load
  // still in flight (its `await` resumes on a removed element otherwise).
  onDestroy: (el, s) => {
    el._railGone = true
    if (el._railPollTimer) {
      clearInterval(el._railPollTimer)
      el._railPollTimer = null
    }
  },

  // ── Header ───────────────────────────────────────────────────────────
  InstallableRailHeader: {
    hide: (el, s) => !el.scope.railHeading(el, s) && !el.scope.railProp(el, s, 'domain')
  },

  // ── Loading skeleton (while first fetch is in flight) ────────────────
  InstallableRailSkeleton: {
    hide: (el, s) => !s.railLoading
  },

  // ── Error banner ─────────────────────────────────────────────────────
  // Reviewed (border-habit sweep): KEPT, flagged not fixed — this is a
  // status-colour-matched border (`red.5` on `red.1`, §4.2 status-tint
  // pattern), not an instance of the ticketed `line`-token class. Still
  // outside §2.5's 4 surviving-stroke exceptions on a strict reading, so a
  // candidate for a future status-banner pass — left for that ticket rather
  // than folded in here.
  ErrorBanner: {
    flow: 'x',
    gap: 'Y',
    align: 'center left',
    padding: 'Y Z',
    round: 'radiusControl',
    background: 'red.1',
    borderWidth: 'hairline',
    borderStyle: 'solid',
    borderColor: 'red.5',
    hide: (el, s) => !s.railError,

    Icon: {
      name: 'exclMark',
      fontSize: 'Z2',
      color: 'red'
    },

    Message: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'red',
      // KEY-IN-STATE: the loader writes a bundle KEY (or the server's own
      // message); the render site resolves it. It used to print the raw key.
      text: (el, s) => el.call('polyglot', s.railError || '')
    },

    RetryButton: {
      tag: 'button',
      type: 'button',
      padding: 'X2 Y',
      round: 'radiusControl',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'Y1',
      color: 'red',
      textDecoration: 'underline',
      text: '{{ common.action.try_again | polyglot }}',
      onClick: (e, el) => {
        const rail = el.scope.railOf(el)
        if (rail) el.scope.reload(rail)
      }
    }
  },

  // ── Empty state ───────────────────────────────────────────────────────
  InstallableRailEmpty: {
    // Show empty only when not loading, no error, and no kinds found.
    hide: (el, s) => !!(s.railLoading || s.railError || s.railKinds.length > 0)
  },

  // ── Rail layout (horizontal scroll strip) ────────────────────────────
  RailStrip: {
    flow: 'x',
    gap: 'Z',
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: 'X2 0',
    // Hide scrollbar visually but keep scroll accessible.
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': { display: 'none' },

    hide: (el, s) => el.scope.stripHidden(el, s, 'rail'),

    childExtends: 'InstallableRailItem',
    childrenAs: 'state',
    children: (el, s) => el.scope.stripRows(el, s, 'rail'),

    // Trailing add button at the end of the chip row.
    // `ignoreChildExtends`: the strip's `childExtends` reaches EVERY child, the
    // named ones included, and the framework MERGES it with a child's own
    // `extends`. Without the opt-out this button was also an
    // InstallableRailItem — three stray icons, an "Available" badge and a
    // chevron inside it, its own label squeezed onto two lines (seen live).
    AddButton: {
      extends: 'InstallableRailAddButton',
      ignoreChildExtends: true
    }
  },

  // ── Grid layout (responsive card grid) ───────────────────────────────
  GridStrip: {
    display: 'grid',
    // Rule 27 — the track is a design-system value. F1 (233px) is the spacing
    // token nearest the old raw 220px minimum; `var(--spacing-F1)` is the form
    // that survives INSIDE minmax() (the grid-track resolver lifts bare
    // letters at the top level only), and `min(100%, …)` stops the track
    // overflowing a pane narrower than one card — the house pairing
    // (workspace components/PersonCard.js PeopleGrid).
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, var(--spacing-F1)), 1fr))',
    gap: 'Z',

    hide: (el, s) => el.scope.stripHidden(el, s, 'grid'),

    childExtends: 'InstallableRailItem',
    childrenAs: 'state',
    children: (el, s) => el.scope.stripRows(el, s, 'grid')
  },

  // ── List layout (vertical management list) ────────────────────────────
  ListStrip: {
    flow: 'y',
    round: 'radiusCard',
    // Fill-contrast card (was a `line` border — border-habit sweep,
    // design-direction.md §8) — the card's own `document` fill already
    // separates it from its container.
    background: 'document',
    overflow: 'hidden',

    hide: (el, s) => el.scope.stripHidden(el, s, 'list'),

    childExtends: 'InstallableRailItem',
    childrenAs: 'state',
    children: (el, s) => el.scope.stripRows(el, s, 'list'),

    // List footer: "Add" button at the bottom of the list.
    ListAddRow: {
      // Not a catalogue row — see RailStrip.AddButton.
      ignoreChildExtends: true,
      flow: 'x',
      gap: 'Y',
      align: 'center left',
      padding: 'Y Z2',
      // Top divider removed (border-habit sweep, design-direction.md §8) —
      // ListStrip's own opaque `document` fill + rounded/`overflow: hidden`
      // wrapper already reads as one continuous card.

      AddButton: {
        extends: 'InstallableRailAddButton'
      }
    }
  }
}

// Test-only surface for the per-domain backend audit (tickets/workspace.md
// "InstallableRail: 4 of 5 domains have no instances backend"). Exporting
// the module-private domain/fetch resolvers lets the offline unit test
// assert the per-domain contract (which domains hit a real backend, that
// mcp needs no orgId while integration does, that a workspace/project
// integration mount with no orgId throws in dev, that mcp self-derives
// kinds from instances) without a DOM/render or a live server. NOT consumed
// by any component code — purely a seam.
export const __installableRailInternals = {
  INSTANCE_BACKED_DOMAINS: _INSTANCE_BACKED_DOMAINS,
  KIND_CATALOG_DOMAINS: _KIND_CATALOG_DOMAINS,
  fetchInstances: _fetchInstances,
  fetchKinds: _fetchKinds,
  deriveSelfKinds: _deriveSelfKinds,
  hasInstalled: _hasInstalled
}
